import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';
import { FirmUser } from './FirmUserAccessPage';
import { formatFirmName } from './firmName';
import AccessTypeOptions from './AccessTypeOptions';

type Props = {
  distro: { id: string; name: string; healthClaimDistroId: string };
  user: FirmUser;
  onClose: (changed: boolean) => void;
};

const ChangeAccessTypeModal = ({ distro, user, onClose }: Props) => {
  const [isClientOnly, setIsClientOnly] = useState<boolean>(!user.isClientOnly);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/api/Advisors/changedistroaccesstype', { distroUserId: user.id, isClientOnly, note: note.trim() });
      toastr.success(`Changed access type for ${user.email}.`);
      onClose(true);
    } catch (e) {
      const err = e as AxiosError;
      toastr.error(typeof err.response?.data === 'string' ? err.response.data : 'Failed to change access type.');
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={() => onClose(false)} centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Change Access Type</Modal.Title>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{user.email}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{formatFirmName(distro)} · Current: {user.accessTypeLabel}</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>New access type <span className="text-danger">*</span></Form.Label>
          <AccessTypeOptions name="cat-access-type" isClientOnly={isClientOnly} onChange={setIsClientOnly} currentIsClientOnly={user.isClientOnly} />
        </Form.Group>
        {isClientOnly
          ? <Alert variant="info" style={{ fontSize: 13 }}>Switching to <strong>Client-Only Firm Access</strong> removes access to financials and commission statements, keeping entire firm client scope only.</Alert>
          : <Alert variant="warning" style={{ fontSize: 13 }}>Switching to <strong>Full Firm Access</strong> grants entire firm financial and client scope, including the ability to <strong>view commission statements</strong> for all advisors at {distro.name}.</Alert>}
        <Form.Group>
          <Form.Label>Ticket number or comment <span className="text-danger">*</span></Form.Label>
          <Form.Control value={note} maxLength={500} onChange={e => setNote(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="primary" disabled={note.trim() === '' || saving} onClick={save}>Save change</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeAccessTypeModal;
